import React, { useState } from 'react';

interface LegalHRPDFExportProps {
  contentRef: React.RefObject<HTMLDivElement>;
  filename: string;
  title: string;
  documentType: string;
  onExportComplete?: (payload: { filename: string; blob: Blob }) => void;
}

const LegalHRPDFExport: React.FC<LegalHRPDFExportProps> = ({ 
  contentRef, 
  filename, 
  title,
  documentType,
  onExportComplete,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const prepareContentForPDF = (contentElement: HTMLElement): string => {
    // Clone the element to avoid modifying the original DOM
    const contentClone = contentElement.cloneNode(true) as HTMLElement;
    
    try {
      // Extract all existing styles
      const extractOriginalStyles = () => {
        let originalStyles = '';
        try {
          originalStyles = Array.from(document.styleSheets)
            .filter(sheet => {
              try {
                return sheet.cssRules.length > 0;
              } catch (e) {
                return false;
              }
            })
            .map(sheet => {
              return Array.from(sheet.cssRules)
                .map(rule => rule.cssText)
                .join('\n');
            }).join('\n');
        } catch (e) {
          console.warn('Error extracting original styles:', e);
        }
        return originalStyles;
      };

      // Create a title page for the PDF
      const createTitlePage = () => {
        const titlePage = document.createElement('div');
        titlePage.id = 'title-page';
        titlePage.classList.add('title-page');
        
        // Style the title page
        const titlePageEl = titlePage as HTMLElement;
        titlePageEl.style.display = 'flex';
        titlePageEl.style.flexDirection = 'column';
        titlePageEl.style.justifyContent = 'center';
        titlePageEl.style.alignItems = 'center';
        titlePageEl.style.height = '100vh';
        titlePageEl.style.margin = '0';
        titlePageEl.style.padding = '2cm';
        titlePageEl.style.textAlign = 'center';
        titlePageEl.style.pageBreakAfter = 'always';
        titlePageEl.style.breakAfter = 'page';
        
        // Create and add title
        const titleEl = document.createElement('h1');
        titleEl.textContent = title;
        (titleEl as HTMLElement).style.fontSize = '36pt';
        (titleEl as HTMLElement).style.color = '#4F46E5'; // Indigo color
        (titleEl as HTMLElement).style.marginBottom = '2cm';
        titlePage.appendChild(titleEl);
        
        // Add document type
        const subtitle = document.createElement('h2');
        subtitle.textContent = documentType;
        (subtitle as HTMLElement).style.fontSize = '20pt';
        (subtitle as HTMLElement).style.marginBottom = '1cm';
        titlePage.appendChild(subtitle);
        
        // Add legal disclaimer
        const disclaimer = document.createElement('p');
        disclaimer.textContent = 'DISCLAIMER: This document was generated using AI and should be reviewed by a qualified legal professional before use.';
        (disclaimer as HTMLElement).style.fontSize = '12pt';
        (disclaimer as HTMLElement).style.color = '#DC2626'; // Red color
        (disclaimer as HTMLElement).style.marginBottom = '1cm';
        titlePage.appendChild(disclaimer);
        
        // Add date
        const date = document.createElement('p');
        date.textContent = 'Generated on ' + new Date().toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        (date as HTMLElement).style.fontSize = '14pt';
        (date as HTMLElement).style.marginTop = '2cm';
        titlePage.appendChild(date);
        
        // Insert at the beginning
        contentClone.insertBefore(titlePage, contentClone.firstChild);
      };
      
      // Add PDF-specific styling
      const styleEl = document.createElement('style');
      styleEl.textContent = `
        ${extractOriginalStyles()}
        
        @page {
          margin: 1.5cm;
          size: A4;
        }
        
        body {
          font-family: 'Arial', 'Helvetica', sans-serif;
          line-height: 1.5;
          color: #333333;
          background-color: #ffffff;
        }
        
        h1, h2, h3, h4, h5, h6 {
          margin-top: 1em;
          margin-bottom: 0.5em;
          page-break-after: avoid;
          break-after: avoid;
          color: #4F46E5;
        }
        
        p {
          margin-bottom: 0.5em;
          orphans: 3;
          widows: 3;
        }
        
        section {
          page-break-before: always;
          break-before: page;
          padding-top: 1cm;
        }
        
        section:first-of-type {
          page-break-before: avoid;
          break-before: avoid;
        }
        
        ul, ol {
          margin-bottom: 1em;
          padding-left: 2em;
        }
        
        li {
          margin-bottom: 0.5em;
        }
        
        .title-page {
          page-break-after: always;
          break-after: page;
        }
        
        img {
          max-width: 100%;
          height: auto;
        }
        
        blockquote {
          margin: 1em 0;
          padding: 0.5em 1em;
          border-left: 4px solid #4F46E5;
          background-color: #f5f5f5;
        }
        
        @media print {
          .no-print {
            display: none !important;
          }
        }

        button, .button {
          display: none;
        }

        /* Hide any UI elements that shouldn't be in the PDF */
        .controls, .header, .footer, form {
          display: none;
        }
      `;
      
      createTitlePage();
      
      // Add page numbers
      const addPageNumbers = () => {
        const pageNumberScript = document.createElement('script');
        pageNumberScript.textContent = `
          // This script will run when the PDF is generated
          function addPageNumbers() {
            const totalPages = document.querySelectorAll('.page').length;
            document.querySelectorAll('.page').forEach((page, index) => {
              const pageNumber = document.createElement('div');
              pageNumber.classList.add('page-number');
              pageNumber.textContent = (index + 1) + ' / ' + totalPages;
              pageNumber.style.position = 'absolute';
              pageNumber.style.bottom = '1cm';
              pageNumber.style.right = '1cm';
              pageNumber.style.fontSize = '10pt';
              pageNumber.style.color = '#888888';
              page.appendChild(pageNumber);
            });
          }
          
          // Call the function when the PDF is being generated
          if (typeof window !== 'undefined') {
            window.addEventListener('DOMContentLoaded', addPageNumbers);
          }
        `;
        contentClone.appendChild(pageNumberScript);
      };
      
      addPageNumbers();
      
      // Add metadata
      const addMetadata = () => {
        const metaTitle = document.createElement('title');
        metaTitle.textContent = title;
        
        const metaAuthor = document.createElement('meta');
        metaAuthor.setAttribute('name', 'author');
        metaAuthor.setAttribute('content', 'Explanatum');
        
        const metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        metaDescription.setAttribute('content', documentType);
        
        // Add metadata to head
        const head = contentClone.querySelector('head') || document.createElement('head');
        head.appendChild(metaTitle);
        head.appendChild(metaAuthor);
        head.appendChild(metaDescription);
        
        if (!contentClone.querySelector('head')) {
          contentClone.insertBefore(head, contentClone.firstChild);
        }
      };
      
      addMetadata();
      
      contentClone.appendChild(styleEl);
      
      return contentClone.outerHTML;
    } catch (error) {
      console.error('Error preparing content for PDF:', error);
      return contentElement.outerHTML;
    }
  };

  const handleExportPDF = async () => {
    if (!contentRef.current) {
      setError('Content reference not found.');
      return;
    }
    
    setIsExporting(true);
    setError(null);
    
    try {
      // Get HTML content and apply PDF-specific styling
      const contentHtml = prepareContentForPDF(contentRef.current);
      
      // Dynamically import html2pdf to avoid bundling issues
      const html2pdf = (await import('html2pdf.js')).default;
      
      // PDF generation options
      const opt = {
        margin: 1,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          letterRendering: true,
          useCORS: true
        },
        jsPDF: { 
          unit: 'cm', 
          format: 'a4', 
          orientation: 'portrait',
          putTotalPages: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      
      // Create a temporary div to hold our styled content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = contentHtml;
      document.body.appendChild(tempDiv);
      
      // Generate PDF from the temporary div
      const pdfInstance = html2pdf().from(tempDiv).set(opt);
      const blob = await pdfInstance.outputPdf('blob');
      await pdfInstance.save();

      if (onExportComplete) {
        onExportComplete({ filename, blob });
      }
      
      // Remove the temporary div after PDF generation
      document.body.removeChild(tempDiv);
      
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button 
      onClick={handleExportPDF}
      disabled={isExporting}
      className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-md flex items-center"
    >
      {isExporting ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Generating PDF...
        </>
      ) : (
        <>
          <svg className="w-4 h-4 mr-2" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path d="M13 8V2H7v6H2l8 8 8-8h-5zM0 18h20v2H0v-2z"/>
          </svg>
          Save as PDF
        </>
      )}
    </button>
  );
};

export default LegalHRPDFExport;
